import { Command } from "cmdk";
import {
  Bookmark,
  Boxes,
  Calculator,
  Database,
  Download,
  FileText,
  FlaskConical,
  FolderKanban,
  FolderPlus,
  GitCompare,
  Github,
  Info,
  Keyboard,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  LineChart,
  Mail,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  SquarePen,
  SunMoon,
  Upload as UploadIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ProfileDot } from "@/components/Profiles/ProfileSwitcher";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAppContext } from "@/context/AppContext";
import { useProfilesContext } from "@/context/ProfileContext";
import { useSampleData } from "@/hooks/useSampleData";
import { useTheme } from "@/hooks/useTheme";
import { normalizedRowsToCsv } from "@/lib/csv/writer";
import { downloadText } from "@/lib/download";
import { exportAllToFile } from "@/lib/profileExport";
import { qualityReportHtml } from "@/lib/reports/report";

const GITHUB_URL = "https://github.com/ghostinator/catalogkit";

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShowShortcuts: () => void;
}

function PaletteItem({
  children,
  icon,
  onSelect,
}: {
  children: ReactNode;
  icon: ReactNode;
  onSelect: () => void;
}): JSX.Element {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground"
    >
      {icon}
      {children}
    </Command.Item>
  );
}

/** Cmd/Ctrl+K command palette (cmdk) for quick navigation and actions. */
export function CommandPalette({
  open,
  onOpenChange,
  onShowShortcuts,
}: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();
  const { toggle } = useTheme();
  const { loadSample } = useSampleData();
  const { result, source } = useAppContext();
  const { profiles, setActiveProfile } = useProfilesContext();

  function run(action: () => void): void {
    onOpenChange(false);
    action();
  }

  const stem = (source?.fileName ?? "catalog").replace(/\.csv$/i, "");
  const switchable = profiles.filter((profile) => !profile.archived);

  async function handleExportAll(): Promise<void> {
    const id = toast.loading("Exporting all data…");
    try {
      await exportAllToFile();
      toast.success("Export downloaded", { id });
    } catch (caught) {
      toast.error("Export failed", {
        id,
        description: caught instanceof Error ? caught.message : String(caught),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0">
        <DialogTitle className="sr-only">Command menu</DialogTitle>
        <Command
          label="Command menu"
          className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          <Command.Input
            placeholder="Type a command or search…"
            className="w-full border-b bg-transparent px-3 py-3 pr-10 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Command.List className="max-h-[60vh] overflow-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </Command.Empty>

            <Command.Group heading="Navigate">
              <PaletteItem icon={<FolderKanban className="h-4 w-4" />} onSelect={() => run(() => navigate("/profiles"))}>
                Profiles
              </PaletteItem>
              <PaletteItem icon={<LayoutDashboard className="h-4 w-4" />} onSelect={() => run(() => navigate("/dashboard"))}>
                Dashboard
              </PaletteItem>
              <PaletteItem icon={<UploadIcon className="h-4 w-4" />} onSelect={() => run(() => navigate("/upload"))}>
                Upload new file
              </PaletteItem>
              <PaletteItem icon={<GitCompare className="h-4 w-4" />} onSelect={() => run(() => navigate("/compare"))}>
                Compare snapshots
              </PaletteItem>
              <PaletteItem icon={<LineChart className="h-4 w-4" />} onSelect={() => run(() => navigate("/quality-trends"))}>
                Quality trends
              </PaletteItem>
              <PaletteItem icon={<Lightbulb className="h-4 w-4" />} onSelect={() => run(() => navigate("/suggestions"))}>
                Categorization suggestions
              </PaletteItem>
              <PaletteItem icon={<Calculator className="h-4 w-4" />} onSelect={() => run(() => navigate("/margin-calculator"))}>
                Margin calculator
              </PaletteItem>
              <PaletteItem icon={<LayoutGrid className="h-4 w-4" />} onSelect={() => run(() => navigate("/process"))}>
                Process / data table
              </PaletteItem>
              <PaletteItem icon={<SquarePen className="h-4 w-4" />} onSelect={() => run(() => navigate("/bulk-edit"))}>
                Bulk edit
              </PaletteItem>
              <PaletteItem icon={<Boxes className="h-4 w-4" />} onSelect={() => run(() => navigate("/vendors"))}>
                Vendors &amp; manufacturers
              </PaletteItem>
              <PaletteItem icon={<Bookmark className="h-4 w-4" />} onSelect={() => run(() => navigate("/saved-searches"))}>
                Saved searches
              </PaletteItem>
              <PaletteItem icon={<ScrollText className="h-4 w-4" />} onSelect={() => run(() => navigate("/audit-log"))}>
                Audit log
              </PaletteItem>
              <PaletteItem icon={<FileText className="h-4 w-4" />} onSelect={() => run(() => navigate("/quotes"))}>
                Quotes
              </PaletteItem>
              <PaletteItem icon={<Mail className="h-4 w-4" />} onSelect={() => run(() => navigate("/contact"))}>
                Contact us
              </PaletteItem>
              <PaletteItem icon={<FileText className="h-4 w-4" />} onSelect={() => run(() => navigate("/report"))}>
                Quality report
              </PaletteItem>
              <PaletteItem icon={<ShieldCheck className="h-4 w-4" />} onSelect={() => run(() => navigate("/privacy-inspector"))}>
                Open privacy inspector
              </PaletteItem>
              <PaletteItem icon={<SettingsIcon className="h-4 w-4" />} onSelect={() => run(() => navigate("/settings"))}>
                Settings
              </PaletteItem>
              <PaletteItem icon={<Info className="h-4 w-4" />} onSelect={() => run(() => navigate("/about"))}>
                About
              </PaletteItem>
            </Command.Group>

            <Command.Group heading="Profiles">
              <PaletteItem
                icon={<FolderPlus className="h-4 w-4" />}
                onSelect={() => run(() => navigate("/profiles?new=1"))}
              >
                Create new profile
              </PaletteItem>
              {switchable.map((profile) => (
                <PaletteItem
                  key={profile.id}
                  icon={<ProfileDot seed={profile.id} />}
                  onSelect={() =>
                    run(() => {
                      setActiveProfile(profile.id);
                      navigate("/dashboard");
                    })
                  }
                >
                  Switch to profile: {profile.label}
                </PaletteItem>
              ))}
            </Command.Group>

            <Command.Group heading="Actions">
              <PaletteItem icon={<FlaskConical className="h-4 w-4" />} onSelect={() => run(() => void loadSample())}>
                Try with sample data
              </PaletteItem>
              <PaletteItem icon={<SunMoon className="h-4 w-4" />} onSelect={() => run(toggle)}>
                Toggle theme
              </PaletteItem>
              <PaletteItem icon={<Database className="h-4 w-4" />} onSelect={() => run(() => void handleExportAll())}>
                Export all data
              </PaletteItem>
              {result ? (
                <PaletteItem
                  icon={<Download className="h-4 w-4" />}
                  onSelect={() =>
                    run(() =>
                      downloadText(
                        `${stem}_cleaned.csv`,
                        normalizedRowsToCsv(result.rows),
                        "text/csv;charset=utf-8",
                      ),
                    )
                  }
                >
                  Download cleaned CSV
                </PaletteItem>
              ) : null}
              {result ? (
                <PaletteItem
                  icon={<Download className="h-4 w-4" />}
                  onSelect={() =>
                    run(() =>
                      downloadText(
                        `${stem}_DATA_ISSUES.html`,
                        qualityReportHtml(result.issues),
                        "text/html;charset=utf-8",
                      ),
                    )
                  }
                >
                  Download quality report (HTML)
                </PaletteItem>
              ) : null}
              <PaletteItem
                icon={<Github className="h-4 w-4" />}
                onSelect={() => run(() => window.open(GITHUB_URL, "_blank", "noopener,noreferrer"))}
              >
                View on GitHub
              </PaletteItem>
              <PaletteItem icon={<Keyboard className="h-4 w-4" />} onSelect={() => run(onShowShortcuts)}>
                Keyboard shortcuts
              </PaletteItem>
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
